// src/pages/DatabaseViewer.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  TableSortLabel,
  Skeleton,
  Divider,
  CircularProgress,
  Stack,
  Container
} from '@mui/material';
import {
  Upload,
  FolderPlus,
  Download,
  Trash2,
  Edit3,
  File,
  Folder,
  Image,
  FileText,
  Archive,
  MoreVert,
  Home,
  HardDrive,
  RefreshCw,
  Database as DatabaseIcon,
  Table as TableIcon,
  Search,
  Code,
  Key,
  Link as LinkIcon,
  Info,
  Plus, Database
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DatabaseViewer = () => {
  const { user } = useAuth();
  const isAdmin = [2, 3].includes(user?.role_id);

  // Estados principais
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableSchema, setTableSchema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  
  // Paginação e filtros
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 50,
    total_rows: 0,
    total_pages: 0
  });
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Query personalizada (Admin apenas)
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);
  const [customQuery, setCustomQuery] = useState('');
  const [queryResults, setQueryResults] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);
  
  // CRUD States
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [insertDialogOpen, setInsertDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [insertFormData, setInsertFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [crudLoading, setCrudLoading] = useState(false);
  
  // Feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Carregar lista de tabelas
  const loadTables = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/database/tables');
      setTables(response.data.tables || []);
    } catch (error) {
      showSnackbar('Erro ao carregar tabelas: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Carregar schema de uma tabela
  const loadTableSchema = async (tableName) => {
    try {
      const response = await api.get(`/api/database/table/${tableName}/schema`);
      setTableSchema(response.data.columns || []);
      return response.data;
    } catch (error) {
      showSnackbar('Erro ao carregar schema: ' + (error.response?.data?.error || error.message), 'error');
      return null;
    }
  };

  // Carregar dados de uma tabela
  const loadTableData = async (tableName, page = 1, pageSize = 50, searchTerm = '', sortCol = '', sortDir = 'asc') => {
    try {
      setDataLoading(true);
      const params = {
        page,
        page_size: pageSize,
        search: searchTerm,
        sort_column: sortCol,
        sort_direction: sortDir.toUpperCase()
      };

      const response = await api.get(`/api/database/table/${tableName}/data`, { params });
      setTableData(response.data.data || []);
      setPagination(response.data.pagination || {});
      return response.data;
    } catch (error) {
      showSnackbar('Erro ao carregar dados: ' + (error.response?.data?.error || error.message), 'error');
      return null;
    } finally {
      setDataLoading(false);
    }
  };

  // Selecionar tabela
  const selectTable = async (table) => {
    setSelectedTable(table);
    setSearch('');
    setSortColumn('');
    setSortDirection('asc');
    await loadTableSchema(table.name);
    await loadTableData(table.name, 1, pagination.page_size);
  };

  // Aplicar filtros
  const applyFilters = () => {
    if (selectedTable) {
      loadTableData(selectedTable.name, 1, pagination.page_size, search, sortColumn, sortDirection);
    }
  };

  // Mudar página
  const handlePageChange = (event, newPage) => {
    if (selectedTable) {
      loadTableData(selectedTable.name, newPage, pagination.page_size, search, sortColumn, sortDirection);
    }
  };

  // Mudar tamanho da página
  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    if (selectedTable) {
      loadTableData(selectedTable.name, 1, newPageSize, search, sortColumn, sortDirection);
    }
  };

  // Ordenação
  const handleSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDirection);
    if (selectedTable) {
      loadTableData(selectedTable.name, pagination.current_page, pagination.page_size, search, column, newDirection);
    }
  };

  // Exportar dados
  const handleExport = async (tableName) => {
    try {
      const response = await api.get(`/api/database/table/${tableName}/export`, {
        params: { format: 'csv', limit: 1000 },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${tableName}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('Exportação iniciada!', 'success');
    } catch (error) {
      showSnackbar('Erro na exportação: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  // CRUD Operations
  const openInsertDialog = () => {
    const initialData = {};
    tableSchema.forEach(col => {
      if (!col.primary_key) {
        initialData[col.name] = '';
      }
    });
    setInsertFormData(initialData);
    setFormErrors({});
    setInsertDialogOpen(true);
  };

  const openEditDialog = async (record) => {
    setSelectedRecord(record);
    try {
      const primaryKey = tableSchema.find(col => col.primary_key)?.name;
      if (!primaryKey) {
        showSnackbar('Tabela não possui chave primária', 'error');
        return;
      }
      const response = await api.get(`/api/database/table/${selectedTable.name}/record/${record[primaryKey]}`);
      setEditFormData(response.data.record);
      setFormErrors({});
      setEditDialogOpen(true);
    } catch (error) {
      showSnackbar('Erro ao carregar registro: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const openDeleteDialog = (record) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleInsert = async () => {
    if (!selectedTable) return;
    try {
      setCrudLoading(true);
      await api.post(`/api/database/table/${selectedTable.name}/insert`, { data: insertFormData });
      showSnackbar('Registro inserido com sucesso!', 'success');
      setInsertDialogOpen(false);
      loadTableData(selectedTable.name, pagination.current_page, pagination.page_size, search, sortColumn, sortDirection);
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      const details = error.response?.data?.details;
      if (details && Array.isArray(details)) {
        setFormErrors(details.reduce((acc, err) => {
          const field = err.split("'")[1];
          if (field) acc[field] = err;
          return acc;
        }, {}));
      }
      showSnackbar('Erro ao inserir: ' + errorMsg, 'error');
    } finally {
      setCrudLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedTable || !selectedRecord) return;
    try {
      setCrudLoading(true);
      const primaryKey = tableSchema.find(col => col.primary_key)?.name;
      if (!primaryKey) {
        showSnackbar('Tabela não possui chave primária', 'error');
        return;
      }
      await api.put(`/api/database/table/${selectedTable.name}/update/${selectedRecord[primaryKey]}`, { data: editFormData });
      showSnackbar('Registro atualizado com sucesso!', 'success');
      setEditDialogOpen(false);
      loadTableData(selectedTable.name, pagination.current_page, pagination.page_size, search, sortColumn, sortDirection);
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      const details = error.response?.data?.details;
      if (details && Array.isArray(details)) {
        setFormErrors(details.reduce((acc, err) => {
          const field = err.split("'")[1];
          if (field) acc[field] = err;
          return acc;
        }, {}));
      }
      showSnackbar('Erro ao atualizar: ' + errorMsg, 'error');
    } finally {
      setCrudLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTable || !selectedRecord) return;
    try {
      setCrudLoading(true);
      const primaryKey = tableSchema.find(col => col.primary_key)?.name;
      if (!primaryKey) {
        showSnackbar('Tabela não possui chave primária', 'error');
        return;
      }
      await api.delete(`/api/database/table/${selectedTable.name}/delete/${selectedRecord[primaryKey]}`);
      showSnackbar('Registro deletado com sucesso!', 'success');
      setDeleteDialogOpen(false);
      loadTableData(selectedTable.name, pagination.current_page, pagination.page_size, search, sortColumn, sortDirection);
    } catch (error) {
      showSnackbar('Erro ao deletar: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setCrudLoading(false);
    }
  };

  // Query personalizada
  const executeCustomQuery = async () => {
    if (!customQuery.trim()) return;
    try {
      setQueryLoading(true);
      const response = await api.post('/api/database/execute-sql-query', { query: customQuery });
      setQueryResults(response.data);
      showSnackbar('Query executada com sucesso!', 'success');
    } catch (error) {
      showSnackbar('Erro na query: ' + (error.response?.data?.error || error.message), 'error');
      setQueryResults(null);
    } finally {
      setQueryLoading(false);
    }
  };

  // Renderizar campo de formulário
  const renderFormField = (column, value, onChange, errors = {}) => {
    const error = errors[column.name];
    const isRequired = ['employees', 'sales', 'hotels', 'groups'].includes(selectedTable?.name);

    if (column.primary_key) {
      return (
        <TextField
          disabled
          fullWidth
          label={`${column.name} (Primary Key)`}
          value={value || ''}
          size="small"
        />
      );
    }

    switch (column.type) {
      case 'boolean':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{column.name}</InputLabel>
            <Select
              value={value || ''}
              label={column.name}
              onChange={(e) => onChange(column.name, e.target.value)}
            >
              <MenuItem value="">Selecione...</MenuItem>
              <MenuItem value={true}>True</MenuItem>
              <MenuItem value={false}>False</MenuItem>
            </Select>
          </FormControl>
        );
      case 'text':
        return (
          <TextField
            multiline
            rows={3}
            fullWidth
            label={column.name}
            value={value || ''}
            onChange={(e) => onChange(column.name, e.target.value)}
            error={!!error}
            helperText={error}
            size="small"
            required={isRequired}
          />
        );
      default:
        return (
          <TextField
            fullWidth
            label={column.name}
            value={value || ''}
            onChange={(e) => onChange(column.name, e.target.value)}
            error={!!error}
            helperText={error || `Type: ${column.type}`}
            size="small"
            required={isRequired}
          />
        );
    }
  };

  // Ícone de coluna
  const getColumnIcon = (column) => {
    if (column.primary_key) return <Key size={16} color="#f57c00" />;
    if (column.foreign_key) return <LinkIcon size={16} color="#1976d2" />;
    return null;
  };

  // Formatar célula
  const formatCellValue = (value, column) => {
    if (value === null || value === undefined) {
      return <Chip label="NULL" size="small" variant="outlined" color="default" />;
    }
    if (typeof value === 'boolean') {
      return <Chip label={value ? 'TRUE' : 'FALSE'} size="small" color={value ? 'success' : 'error'} />;
    }
    if (column.type === 'timestamp' || column.type === 'date') {
      return new Date(value).toLocaleString();
    }
    const strValue = String(value);
    if (strValue.length > 100) {
      return (
        <Tooltip title={strValue}>
          <span>{strValue.substring(0, 100)}...</span>
        </Tooltip>
      );
    }
    return strValue;
  };

  // Snackbar
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  // Efeitos
  useEffect(() => { loadTables(); }, []);

  // Auto-selecionar a primeira tabela ao carregar
  useEffect(() => {
    if (!selectedTable && tables?.length > 0) {
      selectTable(tables[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (search !== undefined) applyFilters();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [search]);

  // Loading screen
  if (loading) {
    return (
      <Box sx={{ 
        position: 'fixed',
        top: '64px', // Space for navbar on both mobile and desktop
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} sx={{ color: '#3b82f6' }} />
          <Typography variant="h6" sx={{ color: '#64748b' }}>
            Loading Database...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // ======= LAYOUT MODERNO E CENTRALIZADO =======
  return (
    <Box sx={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: '#f8fafc',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch'
    }}>
      <Container 
        maxWidth="lg" 
        sx={{ 
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 4 },
          pb: { xs: 8, md: 4 },
          minHeight: '100%'
        }}
      >
        {/* Header com título e ações */}
        <Box sx={{ 
          mb: 4,
          pt: '80px' // Space for navbar on both mobile and desktop
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: { xs: 'flex-start', md: 'center' }, 
            justifyContent: 'space-between',
            mb: 3,
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 0 }
          }}>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  color: '#1e293b',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontSize: { xs: '1.5rem', md: '2.125rem' }
                }}
              >
                <Database size={32} color="#3b82f6" />
                Database Viewer
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#64748b',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                Explore and manage your database tables
              </Typography>
            </Box>

            {/* Ações principais agrupadas */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              {isAdmin && (
                <Button 
                  variant="outlined" 
                  startIcon={<Code size={18} />} 
                  onClick={() => setQueryDialogOpen(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Custom Query
                </Button>
              )}
              <Button 
                variant="outlined" 
                startIcon={<RefreshCw size={18} />} 
                onClick={loadTables} 
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Refresh
              </Button>
            </Stack>
          </Box>

          {/* Card com seletor de tabelas e filtros */}
          <Card 
            elevation={0}
            sx={{ 
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Slider de tabelas */}
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: '#374151'
                  }}
                >
                  Available Tables
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    overflowX: 'auto',
                    pb: 1,
                    '::-webkit-scrollbar': { height: 6 },
                    '::-webkit-scrollbar-thumb': { 
                      bgcolor: '#cbd5e1', 
                      borderRadius: 3 
                    }
                  }}
                >
                  {loading ? (
                    Array.from(new Array(6)).map((_, i) => (
                      <Skeleton 
                        key={i} 
                        variant="rounded" 
                        width={160} 
                        height={48} 
                        sx={{ borderRadius: 3 }}
                      />
                    ))
                  ) : (
                    tables.map((table) => {
                      const active = selectedTable?.name === table.name;
                      return (
                        <Chip
                          key={table.name}
                          icon={<TableIcon size={16} />}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {table.name}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {table.row_count.toLocaleString()} rows
                              </Typography>
                            </Box>
                          }
                          clickable
                          color={active ? 'primary' : 'default'}
                          variant={active ? 'filled' : 'outlined'}
                          onClick={() => selectTable(table)}
                          sx={{
                            height: 'auto',
                            py: 1.5,
                            px: 2,
                            borderRadius: 3,
                            minWidth: 140,
                            '.MuiChip-label': {
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: 0.25
                            },
                            fontWeight: active ? 700 : 500,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme => theme.shadows[4]
                            }
                          }}
                        />
                      );
                    })
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Filtros e ações */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                alignItems: { xs: 'stretch', md: 'center' }, 
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'center', 
                  flex: 1,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <TextField
                    size="small"
                    placeholder="Search records..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={18} />
                        </InputAdornment>
                      )
                    }}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 250 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <FormControl 
                    size="small" 
                    sx={{ 
                      minWidth: { xs: '100%', sm: 120 }
                    }}
                  >
                    <InputLabel>Page Size</InputLabel>
                    <Select 
                      value={pagination.page_size || 50} 
                      label="Page Size" 
                      onChange={handlePageSizeChange}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={200}>200</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Ações da tabela */}
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={1}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                >
                  {selectedTable && (
                    <Button
                      variant="outlined"
                      startIcon={<Download size={16} />}
                      onClick={() => handleExport(selectedTable.name)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Export CSV
                    </Button>
                  )}

                  {isAdmin && selectedTable && (
                    <Button
                      variant="contained"
                      startIcon={<Plus size={16} />}
                      onClick={openInsertDialog}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: theme => theme.shadows[4]
                        }
                      }}
                    >
                      Add Record
                    </Button>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Conteúdo principal */}
        {selectedTable ? (
          <Card 
            elevation={0}
            sx={{ 
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {/* Schema info */}
              <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600,
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#374151'
                  }}
                >
                  <Info size={20} />
                  {selectedTable.name} Schema
                  <Chip 
                    label={`${tableSchema.length} columns`} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tableSchema.map((column) => (
                    <Chip
                      key={column.name}
                      label={`${column.name} (${column.type})`}
                      size="small"
                      variant="outlined"
                      icon={getColumnIcon(column)}
                      color={column.primary_key ? 'warning' : column.foreign_key ? 'info' : 'default'}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Tabela de dados */}
              {dataLoading ? (
                <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer sx={{ maxHeight: { xs: 400, md: 600 } }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {isAdmin && (
                            <TableCell 
                              sx={{ 
                                fontWeight: 700, 
                                width: 100,
                                bgcolor: '#f8fafc'
                              }}
                            >
                              Actions
                            </TableCell>
                          )}
                          {tableSchema.map((column) => (
                            <TableCell
                              key={column.name}
                              sx={{
                                fontWeight: 700,
                                bgcolor: column.primary_key 
                                  ? '#fef3c7' 
                                  : column.foreign_key 
                                    ? '#dbeafe' 
                                    : '#f8fafc'
                              }}
                            >
                              <TableSortLabel
                                active={sortColumn === column.name}
                                direction={sortColumn === column.name ? sortDirection : 'asc'}
                                onClick={() => handleSort(column.name)}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {getColumnIcon(column)}
                                  {column.name}
                                </Box>
                              </TableSortLabel>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableData.map((row, index) => (
                          <TableRow 
                            key={index} 
                            hover
                            sx={{
                              '&:hover': {
                                bgcolor: '#f1f5f9'
                              }
                            }}
                          >
                            {isAdmin && (
                              <TableCell>
                                <Stack direction="row" spacing={0.5}>
                                  <IconButton 
                                    size="small" 
                                    color="primary" 
                                    onClick={() => openEditDialog(row)}
                                    sx={{ borderRadius: 1.5 }}
                                  >
                                    <Edit3 size={16} />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => openDeleteDialog(row)}
                                    sx={{ borderRadius: 1.5 }}
                                  >
                                    <Trash2 size={16} />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            )}
                            {tableSchema.map((column) => (
                              <TableCell key={column.name}>
                                {formatCellValue(row[column.name], column)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Paginação */}
                  <Box sx={{ 
                    p: 3, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderTop: '1px solid #e2e8f0',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 0 }
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {((pagination.current_page - 1) * pagination.page_size) + 1} to {Math.min(pagination.current_page * pagination.page_size, pagination.total_rows)} of {pagination.total_rows} entries
                    </Typography>
                    <Pagination
                      count={pagination.total_pages || 1}
                      page={pagination.current_page || 1}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      size="small"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card 
            elevation={0}
            sx={{ 
              border: '1px solid #e2e8f0',
              borderRadius: 3
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <DatabaseIcon size={80} color="#cbd5e1" />
              <Typography 
                variant="h5" 
                sx={{ 
                  mt: 3,
                  fontWeight: 600,
                  color: '#64748b'
                }}
              >
                Select a table to view its data
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mt: 1,
                  color: '#94a3b8'
                }}
              >
                Choose a table above to explore its structure and data
              </Typography>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Dialogs */}
      <Dialog 
        open={insertDialogOpen} 
        onClose={() => setInsertDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add New Record - {selectedTable?.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {tableSchema.filter(col => !col.primary_key).map((column) => (
              <Grid item xs={12} sm={6} key={column.name}>
                {renderFormField(
                  column,
                  insertFormData[column.name],
                  (field, value) => setInsertFormData(prev => ({ ...prev, [field]: value })),
                  formErrors
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setInsertDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleInsert} 
            variant="contained" 
            disabled={crudLoading} 
            startIcon={crudLoading ? <CircularProgress size={16} /> : <Plus size={16} />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme => theme.shadows[4]
              }
            }}
          >
            {crudLoading ? 'Adding...' : 'Add Record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Record - {selectedTable?.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {tableSchema.map((column) => (
              <Grid item xs={12} sm={6} key={column.name}>
                {renderFormField(
                  column,
                  editFormData[column.name],
                  (field, value) => setEditFormData(prev => ({ ...prev, [field]: value })),
                  formErrors
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdate} 
            variant="contained" 
            disabled={crudLoading} 
            startIcon={crudLoading ? <CircularProgress size={16} /> : <Edit3 size={16} />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme => theme.shadows[4]
              }
            }}
          >
            {crudLoading ? 'Updating...' : 'Update Record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626' }}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete this record from {selectedTable?.name}?
          </Typography>
          {selectedRecord && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: '#fef3c7', 
              borderRadius: 2,
              border: '1px solid #fbbf24'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Record Details:
              </Typography>
              {Object.entries(selectedRecord).slice(0, 3).map(([key, value]) => (
                <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                  <strong>{key}:</strong> {String(value)}
                </Typography>
              ))}
            </Box>
          )}
          <Alert 
            severity="warning" 
            sx={{ 
              mt: 2,
              borderRadius: 2
            }}
          >
            This action cannot be undone!
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error" 
            disabled={crudLoading} 
            startIcon={crudLoading ? <CircularProgress size={16} /> : <Trash2 size={16} />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme => theme.shadows[4]
              }
            }}
          >
            {crudLoading ? 'Deleting...' : 'Delete Record'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={queryDialogOpen} 
        onClose={() => setQueryDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Custom SQL Query
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 2,
              borderRadius: 2
            }}
          >
            Only SELECT queries are allowed. Use with caution.
          </Alert>
          <TextField
            multiline
            rows={8}
            fullWidth
            placeholder="SELECT * FROM table_name WHERE..."
            value={customQuery}
            onChange={(e) => setCustomQuery(e.target.value)}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontFamily: 'monospace'
              }
            }}
          />

          {queryResults && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Results ({queryResults.row_count} rows):
              </Typography>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  maxHeight: 300,
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {queryResults.columns.map((column) => (
                        <TableCell 
                          key={column} 
                          sx={{ 
                            fontWeight: 700,
                            bgcolor: '#f8fafc'
                          }}
                        >
                          {column}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queryResults.data.slice(0, 100).map((row, index) => (
                      <TableRow key={index} hover>
                        {queryResults.columns.map((column) => (
                          <TableCell key={column}>
                            {row[column] !== null ? String(row[column]) : 'NULL'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {queryResults.row_count > 100 && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ mt: 1, display: 'block' }}
                >
                  Showing first 100 rows of {queryResults.row_count} total results
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setQueryDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Close
          </Button>
          <Button 
            onClick={executeCustomQuery} 
            variant="contained" 
            disabled={queryLoading || !customQuery.trim()} 
            startIcon={queryLoading ? <CircularProgress size={16} /> : <Code size={16} />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme => theme.shadows[4]
              }
            }}
          >
            {queryLoading ? 'Executing...' : 'Execute Query'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={closeSnackbar} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: { xs: 24, md: 24 },
          '& .MuiSnackbarContent-root': {
            mb: { xs: 2, md: 0 }
          }
        }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            maxWidth: { xs: 'calc(100vw - 32px)', md: 'none' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DatabaseViewer;